/**
 * Client-side comment/reply placeholders shown before the create API returns.
 */

const TEMP_ID_PREFIX = "temp-";

/**
 * True when this id is a local optimistic placeholder, not a server id.
 * @param {unknown} id
 * @returns {boolean}
 */
export function isOptimisticCommentId(id) {
  return typeof id === "string" && id.startsWith(TEMP_ID_PREFIX);
}

/**
 * Display name for the signed-in account on an optimistic row.
 * @param {object} [user]
 * @returns {string}
 */
export function commentAuthorDisplayName(user) {
  if (!user) {
    return "You";
  }
  if (user.name) {
    return user.name;
  }
  const fullName = `${user.first_name || ""} ${user.last_name || ""}`.trim();
  return fullName || user.username || "You";
}

/**
 * Build a feed-shaped comment used until the server confirms the create.
 * @param {Object} params
 * @param {number} params.userId
 * @param {object} [params.user]
 * @param {number} params.postId
 * @param {number} [params.parentId=0]
 * @param {string} params.content
 * @returns {object}
 */
export function buildOptimisticComment({
  userId,
  user,
  postId,
  parentId = 0,
  content,
}) {
  return {
    id: `${TEMP_ID_PREFIX}${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    post_id: postId,
    parent_id: parentId || 0,
    author_id: userId,
    author_is_org: user?.type === "organization",
    author_name: commentAuthorDisplayName(user),
    author_pfp: user?.pfp_url || "",
    content,
    created_at: new Date().toISOString(),
    like_count: 0,
    dislike_count: 0,
    user_liked: false,
    user_disliked: false,
    user_follows: false,
    reply_count: 0,
    mentions: [],
  };
}

/**
 * Find an optimistic row that matches a confirmed server comment.
 * @param {Array<object>} list
 * @param {object} incoming
 * @returns {string|undefined}
 */
export function findMatchingTempId(list, incoming) {
  if (!incoming) {
    return undefined;
  }
  return (list || []).find(
    (item) =>
      isOptimisticCommentId(item.id) &&
      Number(item.author_id) === Number(incoming.author_id) &&
      item.content === incoming.content
  )?.id;
}

/**
 * Insert or confirm an incoming comment/reply.
 * Replaces a matching optimistic row when present; otherwise prepends.
 * @param {Array<object>} existing
 * @param {object} incoming
 * @param {{ replaceTempId?: string }} [options]
 * @returns {{ list: Array<object>, added: boolean }}
 */
export function mergeIncomingComment(existing, incoming, { replaceTempId } = {}) {
  const list = existing || [];
  if (!incoming || incoming.id == null) {
    return { list, added: false };
  }
  if (list.some((item) => item.id === incoming.id)) {
    if (!replaceTempId) {
      return { list, added: false };
    }
    return { list: list.filter((item) => item.id !== replaceTempId), added: false };
  }
  const tempId = replaceTempId || findMatchingTempId(list, incoming);
  if (tempId) {
    return {
      list: list.map((item) => (item.id === tempId ? incoming : item)),
      added: false,
    };
  }
  return { list: [incoming, ...list], added: true };
}

/**
 * Append a fetched page, swapping any matching optimistic rows in place.
 * @param {Array<object>} existing
 * @param {Array<object>} incoming
 * @returns {Array<object>}
 */
export function mergeFetchedComments(existing, incoming) {
  let list = existing || [];
  const extra = [];
  for (const item of incoming || []) {
    if (!item || item.id == null) {
      continue;
    }
    if (list.some((row) => row.id === item.id)) {
      continue;
    }
    const tempId = findMatchingTempId(list, item);
    if (tempId) {
      list = list.map((row) => (row.id === tempId ? item : row));
    } else {
      extra.push(item);
    }
  }
  return extra.length ? [...list, ...extra] : list;
}

/**
 * Remove a comment/reply by id.
 * @param {Array<object>} existing
 * @param {string|number} id
 * @returns {{ list: Array<object>, removed: boolean }}
 */
export function removeCommentById(existing, id) {
  const list = existing || [];
  if (id == null) {
    return { list, removed: false };
  }
  const next = list.filter((item) => item.id !== id);
  return { list: next, removed: next.length !== list.length };
}
